// Everything the store sells in one list, shared by the header search and the shop page:
// Raymon bikes (table bicicletas), accessories (products) and the Christmas kids' bikes
// (bikes_navidad_2025). Loaded once per visit and kept in sessionStorage for a few minutes.
import { supabase } from '../supabaseClient';
import seo from '../seo/seoConfig.json';
import { slugify, bikePrice, formatPrice } from '../utils';
import { FILTER_GROUPS } from '../data/bikeFilters';
import { inCatalog, prettyName, brandOf, genderOf, ageShort, formatRD, cleanDescription } from '../seo/navidadText';
import { tokenize, slug, fold } from './text';

const CACHE_KEY = 'proomtb-catalog-v5';
const CACHE_TTL = 2 * 60 * 1000;

export const KIND_LABELS = {
    ebike: 'E-Bike',
    bike: 'Bicicleta',
    navidad: 'Bici de Navidad',
    accessory: 'Accesorio'
};

// Words customers type for each bike category, indexed with every bike of that category
export const CATEGORY_WORDS = {
    1: 'e-mtb ebike eléctrica montaña mtb',
    2: 'e-gravel ebike eléctrica gravel',
    3: 'e-trekking ebike eléctrica trekking paseo urbana',
    4: 'e-city ebike eléctrica ciudad urbana city',
    5: 'ruta road carretera',
    6: 'gravel',
    7: 'mtb montaña mountain',
    8: 'trekking paseo híbrida urbana',
    9: 'niños niñas kids infantil junior'
};

// Same for the accessory categories (column products.category)
export const ACCESSORY_WORDS = {
    'Herramientas y Aceites': 'herramienta aceite lubricante taller kit',
    'Puños': 'grips manillas agarre',
    'Bombas y CO2': 'inflador aire bomba',
    'Gafas': 'lentes sol goggles',
    'Guantillas': 'guantes',
    'Licras y Pantalones': 'ropa licra pantalon short',
    'Cascos': 'casco helmet proteccion',
    'Bulticos y Mochilas': 'mochila bolso bultico',
    'Jerseys': 'ropa camiseta jersey',
    'Relojes GPS': 'reloj gps garmin',
    'Termeras': 'termo botella caramanola',
    'Luces': 'luz linterna foco',
    'Nutrición': 'gel energetico suplemento',
    'Ciclo Computadoras GPS': 'ciclocomputador gps velocimetro',
    'Protecciones': 'rodillera codera protector',
    'Componentes': 'componente componentes cadena centro biela plato desviador transmision transmisiones repuesto repuestos pedalier sram shimano',
    'COMPONENTES': 'componente componentes cadena centro biela plato desviador transmision transmisiones repuesto repuestos pedalier sram shimano'
};

// The site photographs some Raymon bikes through the supplier's image proxy
function cleanImageUrl(url) {
    if (typeof url === 'string' && url.includes('raymon-bicycles.com/_next/image')) {
        const match = url.match(/url=([^&]+)/);
        if (match) {
            const decoded = decodeURIComponent(match[1]);
            return decoded.startsWith('http') ? decoded : `https://b2b.raymon-bicycles.com${decoded}`;
        }
    }
    return url || null;
}

const unique = (tokens) => [...new Set(tokens)];
const group = (id) => FILTER_GROUPS.find((g) => g.id === id);
const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();

// 'Guantilllas' is a typo in the database that the accessories page already corrects
function accessoryCategory(value) {
    const category = clean(value);
    return category === 'Guantilllas' ? 'Guantillas' : category;
}

function bikeItem(bike, seriesById) {
    const serie = seriesById[bike.serie_id];
    const categoryId = serie ? Number(serie.categoria_id) : null;
    const category = categoryId ? seo.categories[String(categoryId)] : null;
    const electric = Boolean(categoryId) && categoryId <= 4;
    const wheel = group('rueda').value(bike);
    const motor = group('motor').value(bike);
    const frame = group('cuadro').value(bike);
    const suspension = group('suspension').value(bike);
    const serieName = serie ? serie.nombre : '';
    const suspensionWords = suspension === 'doble' ? 'doble suspension full' : suspension === 'delantera' ? 'hardtail rigida' : '';

    return {
        key: `bike-${bike.id}`,
        kind: electric ? 'ebike' : 'bike',
        name: clean(bike.modelo),
        brand: 'Raymon',
        category: category ? category.name : 'Bicicletas',
        categoryKey: category ? category.slug : 'otras-bicicletas',
        subtitle: [category && category.name, motor && `motor ${motor}`, wheel && `aro ${wheel.replace('/', '" / ')}"`].filter(Boolean).join(' · '),
        price: bikePrice(bike),
        currency: 'USD',
        image: cleanImageUrl((bike.imagenes_urls || [])[0]),
        url: `/product/${slugify(bike.modelo)}`,
        wheel: wheel || null,
        serie: serieName || null,
        serieKey: serieName ? slug(serieName) : null,
        suspension: suspension || null,
        frame: frame || null,
        motor: motor || null,
        popular: false,
        created: bike.created_at || null,
        words: {
            name: unique(tokenize(`Raymon ${bike.modelo} ${serieName}`)),
            // What the product is: weighs like its name, so 'mtb' lists the MTB bikes before a pedal called "MTB"
            category: unique(tokenize(`bicicleta ${category ? category.name : ''} ${CATEGORY_WORDS[categoryId] || ''} ${electric ? 'ebike electrica' : 'analoga analogas'}`)),
            meta: unique(tokenize([
                'bicicleta raymon', electric ? 'ebike electrica' : 'analoga analogas convencional',
                wheel ? `aro ${wheel} rueda ${wheel}` : '', frame || '', suspensionWords, motor || ''
            ].join(' '))),
            spec: unique(tokenize([
                bike.motor_modelo, bike.cuadro_material, (bike.colores || []).join(' '), (bike.sizes || []).join(' '),
                bike.transmision_modelo, bike.frenos_modelo, bike.horquilla, bike.description
            ].filter(Boolean).join(' ')))
        }
    };
}

function accessoryItem(product) {
    const name = clean(product.name);
    const category = accessoryCategory(product.category);
    // The brand column historically had an unreliable default ("Orbea" was placed on everything).
    // Any brand explicitly configured other than legacy unmentioned "Orbea" is preserved.
    const rawBrand = clean(product.brand);
    const isUnreliableLegacyOrbea = rawBrand.toLowerCase() === 'orbea' && !fold(name).includes('orbea');
    const brand = rawBrand && !isUnreliableLegacyOrbea ? rawBrand : null;
    const price = parseFloat(Array.isArray(product.price) ? product.price[0] : product.price);

    return {
        key: `accessory-${product.id}`,
        kind: 'accessory',
        name,
        brand,
        category: category || 'Accesorios',
        categoryKey: slug(category) || 'otros-accesorios',
        subtitle: [category, brand].filter(Boolean).join(' · '),
        price: Number.isFinite(price) && price > 0 ? price : null,
        currency: product.currency || 'DOP',
        image: cleanImageUrl(product.image_url),
        url: `/product/${slugify(product.name)}`,
        wheel: null,
        popular: product.section === 'mas_vendidos',
        created: product.created_at || null,
        words: {
            name: unique(tokenize(name)),
            category: unique(tokenize(`${category} ${ACCESSORY_WORDS[category] || ''}`)),
            meta: unique(tokenize(`${brand || ''} accesorio accesorios`)),
            spec: unique(tokenize(clean(product.description))).slice(0, 40)
        }
    };
}

function navidadItem(bike) {
    const name = prettyName(bike);
    const brand = brandOf(bike);
    const aro = String(bike.categoria);
    const gender = genderOf(bike);
    const genderWords = { nina: 'niña niñas girl princesa', nino: 'niño niños boy', unisex: 'niño niña unisex' }[gender];
    const extras = [/bmx/i.test(bike.nombre) ? 'bmx' : '', /balance|walker/i.test(bike.nombre) ? 'balance sin pedales equilibrio' : ''];

    return {
        key: `navidad-${bike.id}`,
        kind: 'navidad',
        name,
        brand,
        category: `Niños aro ${aro}`,
        categoryKey: `aro-${aro}`,
        subtitle: `Aro ${aro} · ${ageShort(bike)}`,
        price: Math.round(Number(bike.precio)) || null,
        currency: 'DOP',
        image: bike.imagen_url || null,
        url: `/navidad/${bike.slug}/`,
        wheel: aro,
        gender,
        popular: true,
        created: bike.created_at || null,
        words: {
            name: unique(tokenize(`${name} ${brand}`)),
            category: unique(tokenize(`bicicleta niños niñas kids infantil navidad aro ${aro} ${genderWords}`)),
            meta: unique(tokenize(`regalo reyes nochebuena rueda ${aro} ${ageShort(bike)} ${extras.join(' ')}`)),
            spec: unique(tokenize(cleanDescription(bike.descripcion)))
        }
    };
}

async function fetchAll() {
    const [bikes, series, products, navidad] = await Promise.all([
        supabase.from('bicicletas').select('id, modelo, serie_id, precio_eur, imagenes_urls, cuadro_material, neumaticos, tire_f, shock, amortiguador, tipos_marco, recorrido_mm, motor_modelo, colores, sizes, transmision_modelo, frenos_modelo, horquilla, description, created_at'),
        supabase.from('series').select('id, nombre, categoria_id'),
        supabase.from('products').select('id, name, category, price, currency, image_url, brand, description, section, created_at'),
        supabase.from('bikes_navidad_2025').select('*')
    ]);
    [bikes, series, products, navidad].forEach((result) => {
        if (result.error) console.error('Error loading the store catalog:', result.error);
    });

    const seriesById = Object.fromEntries((series.data || []).map((s) => [s.id, s]));
    // Two accessory rows with the same name share one page, so only the first one is listed
    const seen = new Set();
    const accessories = (products.data || []).filter((p) => p.name).map(accessoryItem).filter((a) => !seen.has(a.url) && seen.add(a.url));
    return [
        ...(bikes.data || []).filter((b) => b.modelo).map((b) => bikeItem(b, seriesById)),
        ...inCatalog(navidad.data || []).map(navidadItem),
        ...accessories
    ];
}

function readCache() {
    try {
        const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
        if (cached && cached.at && Date.now() - cached.at < CACHE_TTL && Array.isArray(cached.items) && cached.items.length) return cached.items;
    } catch (e) { /* no storage, or an old shape: load again */ }
    return null;
}

function writeCache(items) {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), items }));
    } catch (e) { /* private mode or full storage: the list still lives in memory */ }
}

let pending = null;

export function clearCatalogCache() {
    pending = null;
    try {
        sessionStorage.removeItem(CACHE_KEY);
    } catch (e) { /* ignore */ }
}

// The full list, loaded once; every caller shares the same promise
export function loadCatalog() {
    if (pending) return pending;
    const cached = readCache();
    if (cached) {
        pending = Promise.resolve(cached);
        return pending;
    }
    pending = fetchAll().then((items) => {
        if (items.length) writeCache(items);
        return items;
    }).catch((error) => {
        pending = null;
        throw error;
    });
    return pending;
}

// 'USD $1,234.00' for Raymon, 'RD$ 2,600.00' for accessories and 'RD$ 17,000' for Christmas bikes
export function itemPrice(item) {
    if (!item.price) return 'Precio a consultar';
    if (item.kind === 'navidad') return `RD$ ${formatRD(item.price)}`;
    return formatPrice(item.price, item.currency === 'USD' ? 'bikes' : 'accessories');
}
